<template>
  <div class="admin-settings">
    <div v-if="!isAdmin" class="not-allowed">
      <v-icon size="48" color="#EA5545">mdi-alert-circle</v-icon>
      <p>Diese Seite ist nur für Admin-Benutzer verfügbar.</p>
    </div>
    <div v-else>
      <v-card class="settings-card" elevation="1">
        <v-card-title class="settings-title">
          <v-icon left color="#EA5545">mdi-account-cog</v-icon>
          Admin Einstellungen
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div class="thumb-section">
            <div class="thumb-preview">
              <img
                v-if="thumbUrl"
                :src="thumbUrl"
                alt="Admin Thumbnail"
              />
              <div v-else class="thumb-placeholder">
                <v-icon size="40" color="#999">mdi-account</v-icon>
                <span>Kein Thumbnail vorhanden</span>
              </div>
            </div>
            <div class="thumb-actions">
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="handleFileChange"
              />
              <v-btn
                color="#EA5545"
                variant="outlined"
                @click="$refs.fileInput.click()"
                :loading="isUploading"
                :disabled="isUploading"
              >
                <v-icon left>mdi-upload</v-icon>
                Thumbnail hochladen
              </v-btn>
              <v-btn
                color="error"
                variant="text"
                @click="removeThumbnail"
                :disabled="!hasThumb || isDeleting"
                :loading="isDeleting"
              >
                <v-icon left>mdi-delete</v-icon>
                Thumbnail löschen
              </v-btn>
            </div>
          </div>
          <v-alert
            v-if="uploadError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ uploadError }}
          </v-alert>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
const successStore = inject('successStore')
const errorStore = inject('errorStore')
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const fileInput = ref(null)
const isUploading = ref(false)
const isDeleting = ref(false)
const uploadError = ref('')

const userData = computed(() => userStore?.userData || null)

const isAdmin = computed(() => {
  const u = userData.value
  if (!u || !u.user_admin) return false
  return Number(u.user_admin) === 1
})

const currentThumbHash = computed(() => userData.value?.user_thumb || null)

const thumbUrl = computed(() => {
  if (!currentThumbHash.value) return null
  return `/getfile/${currentThumbHash.value}`
})

const hasThumb = computed(() => !!currentThumbHash.value)

function handleFileChange(event) {
  const file = event.target?.files?.[0]
  if (!file) return
  uploadThumbnail(file)
  event.target.value = ''
}

async function uploadThumbnail(file) {
  if (!isAdmin.value) return
  uploadError.value = ''
  const formData = new FormData()
  formData.append('files[]', file)
  formData.append('file_net_id', 'user_thumb')
  isUploading.value = true
  try {
    const response = await baseCalls.userUpload(formData)
    if (response.data.success) {
      const thumbHash = response.data.thumb_hash ?? null
      if (thumbHash) {
        userStore.setUser({
          ...(userData.value || {}),
          user_thumb: thumbHash
        })
      }
      successStore?.setSuccess?.(true, 'Thumbnail aktualisiert', 'Dein Admin-Bild wurde erfolgreich gespeichert.')
    } else {
      uploadError.value = response.data.message || 'Upload fehlgeschlagen.'
    }
  } catch (err) {
    console.error('Error uploading thumbnail:', err)
    uploadError.value = 'Beim Upload ist ein Fehler aufgetreten.'
    errorStore?.setError?.(true, uploadError.value)
  } finally {
    isUploading.value = false
  }
}

async function removeThumbnail() {
  if (!isAdmin.value || !hasThumb.value) return
  uploadError.value = ''
  isDeleting.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'deleteuserthumb'
    })
    if (response.data.success) {
      userStore.setUser({
        ...(userData.value || {}),
        user_thumb: null
      })
      successStore?.setSuccess?.(true, 'Thumbnail entfernt', 'Dein Admin-Bild wurde gelöscht.')
    } else {
      uploadError.value = response.data.message || 'Thumbnail konnte nicht gelöscht werden.'
    }
  } catch (err) {
    console.error('Error deleting thumbnail:', err)
    uploadError.value = 'Beim Löschen ist ein Fehler aufgetreten.'
    errorStore?.setError?.(true, uploadError.value)
  } finally {
    isDeleting.value = false
  }
}
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminSettings.css"></style>
